import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import * as R from 'ramda';

import CreateAssetModalContext, {
  ModalStep,
  RouteDataState,
  RouteOptionsState,
} from '@atom/components/common/createAssetModal/CreateAssetModalContext';
import { CREATE_ASSET } from '@atom/graph/asset';
import { GET_SCHEMA } from '@atom/graph/schema';
import { Location, useCurrentLocation } from '@atom/hooks/useCurrentLocation';
import { useNavigation } from '@atom/hooks/useNavigation';
import { usePreferences } from '@atom/hooks/usePreferences';
import { AtomMapProvider } from '@atom/map';
import { Button, Icon, Modal, Snackbar } from '@atom/mui';
import { ChevronLeftIcon, ChevronRightIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { AssetCreateAttributesInput } from '@atom/types/asset';
import { InventoryCategoryTree } from '@atom/types/category';
import {
  AssetTemplateSegment,
  AssetTemplateSegmentSource,
} from '@atom/types/preferences';
import { Schema } from '@atom/types/schema';
import {
  cleanAttributes,
  getAttributeErrors,
} from '@atom/utilities/assetUtilities';
import {
  getErrorCode,
  isNilOrEmpty,
} from '@atom/utilities/validationUtilities';

import AssetRouteTab from './assetRouteTab/AssetRouteTab';
import { mapRouteToAttributes } from './assetRouteTab/assetRouteUtilities';
import AttributesTab from './attributesTab/AttributesTab';
import LineLocationTab from './lineLocationTab/LineLocationTab';
import PointLocationTab from './pointLocationTab/PointLocationTab';
import CostSettingsTab from './CostSettingsTab';
import {
  getModalStepsArray,
  isCurrentStepDisabled,
} from './createAssetUtilities';
import FolderSelectionTab from './FolderSelectionTab';
import NameTab from './NameTab';
import SchemaSelectionTab from './SchemaSelectionTab';
import StepperSidebar from './StepperSidebar';

import classes from './createAssetModal.module.css';

const styles = {
  modalContent: {
    padding: 0,
  },
  back: {
    marginRight: '1rem',
  },
};

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * If a leaf node category is passed in, it will be used to populate the schema and category steps
   */
  initialCategory?: InventoryCategoryTree;
}

const getPresetName = (
  attributes: AssetCreateAttributesInput,
  templates: AssetTemplateSegment[],
): string => {
  return templates
    .filter(
      segment => segment.type !== AssetTemplateSegmentSource.COLLISION_COUNTER,
    )
    .map(segment => {
      return `${segment.prefix || ''}${attributes[segment.id]?.value || ''}${segment.suffix || ''}`;
    })
    .join('');
};

const CreateAssetModal = ({ open, onClose, initialCategory }: Props) => {
  const [, { navigate }] = useNavigation();
  const { pointLocation } = useCurrentLocation();
  const preferences = usePreferences();

  const assetNaming = preferences?.inventory?.assetNaming;

  const [activeStep, setActiveStep] = useState<ModalStep>(ModalStep.TYPE);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<String>>(
    new Set([]),
  );
  const [openCategories, setOpenCategories] = useState<Set<string>>(null);

  const [schema, setSchema] = useState<Schema>();
  const [category, setCategory] = useState<InventoryCategoryTree>();
  const [location, setLocation] = useState<Location>();
  const [address, setAddress] = useState<string>('');
  const [locationFromSearch, setLocationFromSearch] = useState<boolean>(false);
  const [rate, setRate] = useState<number>();
  const [unit, setUnit] = useState<string>('');
  const [isStockBased, setIsStockBased] = useState<boolean>(false);
  const [isStartEndReading, setIsStartEndReading] = useState<boolean>(false);
  const [isStartEndCalculated, setIsStartEndCalculated] =
    useState<boolean>(false);
  const [quantityOnHand, setQuantityOnHand] = useState<number>(0);
  const [name, setName] = useState<string>('');
  const [disableName, setDisableName] = useState<boolean>(false);
  const [attributes, setAttributes] = useState<AssetCreateAttributesInput>();
  const [errorAttributeIds, setErrorAttributeIds] = useState<Set<string>>(
    new Set([]),
  );

  // ALDOT related state
  const [routeData, setRouteData] = useState<RouteDataState>({});
  const [routeOptions, setRouteOptions] = useState<RouteOptionsState>({});
  const [milePost, setMilePost] = useState<number>(null);
  const [milePostRanges, setMilePostRanges] = useState<number[][]>([]);
  const [isRouteSnapped, setIsRouteSnapped] = useState<boolean>(false);
  // Set by a snapped route autocomplete response so the location and route
  // data it writes are not treated as user changes: the route tab skips its
  // next autocomplete and the location effect below skips the route reset.
  // Both read it from the same render, so it must be state, not a ref.
  const [currentRouteAutoCompleteDone, setCurrentRouteAutoCompleteDone] =
    useState<boolean>(false);

  const [createAsset, { loading: createAssetLoading }] =
    useMutation(CREATE_ASSET);

  const [getSchema, { loading }] = useLazyQuery(GET_SCHEMA, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    const getSchemaRequest = async () => {
      const { data } = await getSchema({
        variables: { id: initialCategory?.schemaId },
      });

      setSchema(data?.schema || null);
    };

    if (open) {
      if (initialCategory?.schemaId) {
        getSchemaRequest();
      }

      if (initialCategory?.id !== 'inventory') {
        setCategory(initialCategory);
      }
    }
  }, [open, initialCategory]);

  const resetValues = () => {
    setActiveStep(ModalStep.TYPE);
    setCollapsedGroups(new Set([]));
    setOpenCategories(null);
    setErrorAttributeIds(new Set([]));
    setRate(null);
    setUnit('');
    setIsStockBased(false);
    setIsStartEndReading(false);
    setIsStartEndCalculated(false);
    setQuantityOnHand(0);
    setName('');
    setAttributes(null);
    setLocation(null);
    setAddress('');
    setLocationFromSearch(false);
    setRouteData({});
    setRouteOptions({});
    setMilePost(null);
    setMilePostRanges([]);
    setIsRouteSnapped(false);
    setCurrentRouteAutoCompleteDone(false);
  };

  const onCloseModal = () => {
    setSchema(null);
    setCategory(null);
    resetValues();
    onClose();
  };

  useEffect(() => {
    // Only reset the currently selected category if it does not match the new schema
    if (
      category?.schemaId !== schema?.id &&
      !isNilOrEmpty(category?.schemaId)
    ) {
      setCategory(null);
    }

    resetValues();
  }, [schema]);

  useEffect(() => {
    if (!R.isNil(schema?.locationType)) {
      setLocation(schema.locationType === 'LineString' ? null : pointLocation);
    }
  }, [schema]);

  useEffect(() => {
    // Snapping refines the location onto the selected route, so unlike a
    // location the user picks it must not reset the route data.
    if (currentRouteAutoCompleteDone) {
      setCurrentRouteAutoCompleteDone(false);
      return;
    }

    // ALDOT: change in location should reset route data
    setRouteData({});
    setRouteOptions({});
    setMilePost(null);
    setMilePostRanges([]);
    setIsRouteSnapped(false);
  }, [location]);

  const STEPS_ARRAY = getModalStepsArray(schema);

  const isConfirmDisabled = isCurrentStepDisabled({
    STEPS_ARRAY,
    schema,
    category,
    activeStep,
    location,
    routeData,
    routeOptions,
    milePost,
    milePostRanges,
    isRouteSnapped,
    rate,
    unit,
    isStockBased,
    quantityOnHand,
    name,
    errorAttributeIds,
  });

  const confirm = async () => {
    const includeLocation = !R.isNil(schema?.locationType);

    const payload = {
      ...(!schema.lrsRequired ? { name, externalId: name } : {}),
      schemaId: schema.id,
      ...(!schema.lrsRequired ? { categoryId: category.id } : {}),
      attributes: cleanAttributes(attributes),
      ...(includeLocation ? { location } : {}),
      ...(schema.isMaterial
        ? {
            budget: {
              rate,
              unit,
              quantityOnHand,
              isStockBased,
              isStartEndReading,
              isStartEndCalculated,
            },
          }
        : {}),
    };

    try {
      const { data } = await createAsset({
        variables: {
          input: payload,
        },
      });

      navigate(`/inventory/${data?.assetCreate?.id}`);
    } catch (err) {
      const message =
        getErrorCode(err) === 422
          ? 'Inventory Type is not published.'
          : 'An error occurred. Please try again.';
      Snackbar.error({ message });
    } finally {
      onCloseModal();
    }
  };

  const getContent = (): React.ReactNode => {
    const content = {
      [ModalStep.TYPE]: <SchemaSelectionTab />,
      [ModalStep.FOLDER]: <FolderSelectionTab />,
      [ModalStep.LOCATION]:
        schema?.locationType === 'LineString' ? (
          <LineLocationTab />
        ) : (
          <PointLocationTab />
        ),
      [ModalStep.ROUTE]: <AssetRouteTab />,
      [ModalStep.COST]: <CostSettingsTab />,
      [ModalStep.ATTRIBUTES]: <AttributesTab />,
      [ModalStep.NAME]: <NameTab />,
    };

    return content[activeStep];
  };

  const handleStep = (value: number) => {
    const currentStepIndex = R.findIndex(R.propEq(activeStep, 'value'))(
      STEPS_ARRAY,
    );

    const currentStep = STEPS_ARRAY[currentStepIndex];

    if (currentStep.value === ModalStep.ATTRIBUTES && value > 0) {
      const errors = getAttributeErrors(schema, attributes);

      if (errors.size) {
        setErrorAttributeIds(new Set(errors.keys()));
        return;
      }
    }

    if (currentStep.value === ModalStep.ATTRIBUTES) {
      const template = assetNaming?.[schema.id];
      setName(template ? getPresetName(attributes, template) : '');
      setDisableName(!!template);
    }

    // Merges the custom ALDOT data into attributes
    if (currentStep.value === ModalStep.ROUTE && value > 0) {
      setAttributes({
        ...attributes,
        ...mapRouteToAttributes(schema, routeData, milePost),
      });
    }

    const newStep = STEPS_ARRAY[currentStepIndex + value];

    setActiveStep(newStep.value);
  };

  const getFooter = () => {
    const showCreateButton =
      R.findIndex(R.propEq(activeStep, 'value'), STEPS_ARRAY) ===
      STEPS_ARRAY.length - 1;
    const showBackButton = activeStep !== ModalStep.TYPE;

    const isDisabled = isConfirmDisabled || createAssetLoading;

    const rightArrowColor = isDisabled
      ? colors.neutral.gray
      : colors.neutral.white;

    return (
      <div className={classes.footer}>
        {showBackButton ? (
          <Button
            onClick={() => handleStep(-1)}
            data-cy="createAssetModalBackButton"
          >
            <Icon icon={ChevronLeftIcon} />
            Back
          </Button>
        ) : (
          <div />
        )}
        <div>
          <Button
            onClick={onCloseModal}
            style={{ marginRight: '0.5rem' }}
            data-cy="createAssetModalCancelButton"
          >
            Cancel
          </Button>
          {showCreateButton ? (
            <Button
              color="primary"
              variant="contained"
              onClick={confirm}
              disabled={isDisabled}
              data-cy="createAssetModalCreateButton"
            >
              Create
            </Button>
          ) : (
            <Button
              color="primary"
              variant="contained"
              onClick={() => handleStep(1)}
              disabled={isDisabled}
              data-cy="createAssetModalNextButton"
            >
              Next
              <Icon color={rightArrowColor} icon={ChevronRightIcon} />
            </Button>
          )}
        </div>
      </div>
    );
  };

  const modalSize =
    activeStep === ModalStep.ATTRIBUTES || activeStep === ModalStep.LOCATION
      ? '55rem'
      : '50rem';
  const contentHeight =
    activeStep === ModalStep.ATTRIBUTES || activeStep === ModalStep.LOCATION
      ? '37.5rem'
      : '32.70rem';
  const content = getContent();

  return (
    <CreateAssetModalContext.Provider
      value={{
        activeStep,
        setActiveStep,
        STEPS_ARRAY,
        collapsedGroups,
        setCollapsedGroups,
        openCategories,
        setOpenCategories,
        schema,
        setSchema,
        category,
        setCategory,
        location,
        setLocation,
        address,
        setAddress,
        rate,
        setRate,
        unit,
        setUnit,
        isStockBased,
        setIsStockBased,
        isStartEndReading,
        setIsStartEndReading,
        isStartEndCalculated,
        setIsStartEndCalculated,
        quantityOnHand,
        setQuantityOnHand,
        name,
        setName,
        attributes,
        setAttributes,
        errorAttributeIds,
        setErrorAttributeIds,
        locationFromSearch,
        setLocationFromSearch,
        routeData,
        setRouteData,
        routeOptions,
        setRouteOptions,
        milePost,
        setMilePost,
        milePostRanges,
        setMilePostRanges,
        isRouteSnapped,
        setIsRouteSnapped,
        currentRouteAutoCompleteDone,
        setCurrentRouteAutoCompleteDone,
        disableName,
      }}
    >
      <Modal
        title="Create Inventory"
        footer={getFooter()}
        open={open}
        contentStyle={styles.modalContent}
        width={modalSize}
        loading={loading}
        data-cy="createAssetModal"
        disableCloseOnOutsideClick
      >
        <AtomMapProvider>
          <div
            className={classes.modalContent}
            style={{ minHeight: contentHeight }}
          >
            <StepperSidebar />
            {content}
          </div>
        </AtomMapProvider>
      </Modal>
    </CreateAssetModalContext.Provider>
  );
};

export default CreateAssetModal;
